import { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import { featureAccess } from "../permissions/access";
import { deleteDemandAdjustment } from "../services";
import { getBranchCode } from "../services/keycloak";
import { DemandAdjustment } from "../types/demand-factor.type";
import { useUserRole } from "../utils/hooks/useUserRole";
import ConfirmModal from "../components/ConfirmModal";
import DemandFactorFilter from "../components/DemandFactor/Filter";
import DemandFactorFormDialog from "../components/DemandFactor/FormDialog";
import PreviewDialog from "../components/DemandFactor/PreviewDialog";
import DemandFactorTable from "../components/DemandFactor/Table";
import { useDemandFactorLookups } from "../components/DemandFactor/useDemandFactorLookups";
import { UNNAMED_ROW } from "../components/DemandFactor/constants";
import {
  DemandFactorFilters,
  INITIAL_FILTERS,
  useDemandAdjustmentList,
} from "../components/DemandFactor/useDemandAdjustmentList";

/** Answers whether the API removed the row. */
const removeRow = async (id: number): Promise<boolean> => {
  try {
    await deleteDemandAdjustment(id);
    return true;
  } catch {
    // The axios interceptor already showed why; the confirm stays open to retry or cancel.
    return false;
  }
};

export default function DemandFactorPage() {
  const role = useUserRole();
  const canSelectBranch = featureAccess.demandFactorAllBranches(role);
  // Decoded once: the token's branch does not change while the page is open.
  const ownBranchCode = useMemo(
    () => (canSelectBranch ? null : getBranchCode()),
    [canSelectBranch],
  );
  const lookups = useDemandFactorLookups(canSelectBranch);

  const [filters, setFilters] = useState<DemandFactorFilters>(INITIAL_FILTERS);
  const [reloadKey, setReloadKey] = useState<number>(0);
  const list = useDemandAdjustmentList(filters, reloadKey);

  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [editingRow, setEditingRow] = useState<DemandAdjustment | null>(null);
  const [deletingRow, setDeletingRow] = useState<DemandAdjustment | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);

  const reload = () => setReloadKey((previous) => previous + 1);

  const handleFilterChange = (patch: Partial<DemandFactorFilters>) => {
    setFilters((previous) => ({ ...previous, ...patch }));
  };

  const handleCreate = () => {
    setEditingRow(null);
    setIsFormOpen(true);
  };

  const handleEdit = (row: DemandAdjustment) => {
    setEditingRow(row);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => setIsFormOpen(false);

  const handleSaved = () => {
    setIsFormOpen(false);
    reload();
  };

  const handleAskDelete = (row: DemandAdjustment) => setDeletingRow(row);

  const handleCloseDelete = () => {
    if (!isDeleting) setDeletingRow(null);
  };

  const handleConfirmDelete = async () => {
    if (!deletingRow) return;
    setIsDeleting(true);
    const removed = await removeRow(deletingRow.id);
    setIsDeleting(false);
    if (!removed) return;
    setDeletingRow(null);
    reload();
  };

  const handleOpenPreview = () => setIsPreviewOpen(true);
  const handleClosePreview = () => setIsPreviewOpen(false);

  return (
    <Box sx={{ padding: "8px 0" }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
        gap={2}
        mb={2}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Demand Factor
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ตัวคูณยอดแนะนำตามเทศกาล ฤดูกาล โปรโมชัน และการเติบโต · หลายแถวที่ตรงร้านเดียวกันจะคูณกันทั้งหมด
          </Typography>
        </Box>
        <Stack direction="row" gap={1}>
          <Button
            variant="outlined"
            startIcon={<CalculateOutlinedIcon />}
            onClick={handleOpenPreview}
          >
            ทดลองคำนวณ
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
            เพิ่ม Demand Factor
          </Button>
        </Stack>
      </Stack>

      <Paper sx={{ padding: "24px 16px", mb: 2 }}>
        <DemandFactorFilter
          canSelectBranch={canSelectBranch}
          branches={lookups.branches}
          ownBranchCode={ownBranchCode}
          filters={filters}
          onChange={handleFilterChange}
        />
      </Paper>

      <DemandFactorTable
        list={list}
        names={lookups}
        onEdit={handleEdit}
        onDelete={handleAskDelete}
      />

      <DemandFactorFormDialog
        open={isFormOpen}
        row={editingRow}
        canSelectBranch={canSelectBranch}
        ownBranchCode={ownBranchCode}
        lookups={lookups}
        enabledTypes={list.enabledTypes}
        onClose={handleCloseForm}
        onSaved={handleSaved}
      />

      <ConfirmModal
        open={Boolean(deletingRow)}
        title={`ลบ Demand Factor "${deletingRow?.name ?? UNNAMED_ROW}"?`}
        description="แถวนี้จะไม่ถูกนำไปคำนวณยอดแนะนำอีก"
        confirmLabel="ลบ"
        confirmColor="error"
        loading={isDeleting}
        onClose={handleCloseDelete}
        onConfirm={handleConfirmDelete}
      />

      <PreviewDialog
        open={isPreviewOpen}
        onClose={handleClosePreview}
        canSelectBranch={canSelectBranch}
        lookups={lookups}
      />
    </Box>
  );
}
